/**
 * Floor plans as JSON: one room for the room editor (which can paste it
 * back), and every room of the property for the floor plan view (an array
 * with one entry per room, led by the room's id, then the host's description
 * of the room, by default its name, then its floor plan, placement included).
 * For handing a real room or property to a test or a colleague. A room paste
 * parses through the data model's own `parseFloorPlan`, keeps the current
 * room's whole-property placement, and says why when the JSON won't do.
 */
import { type FloorPlanRoom, getOptimizedFloorPlan, parseFloorPlan } from '@kaiizen/kai-floor-plan';
import { type AssemblyRoom, withPlacementOf } from '../assembler';

/** What a copy or a paste did: done, or why it changed nothing. */
export interface JsonPasteOutcome {
  ok: boolean;
  message: string;
}

/** Text that could be a JSON object: what a room paste would try. */
const looksLikeJsonObject = (text: string): boolean => /^\s*\{/.test(text);

/** Text that could be JSON at all, an object or a list: what the room editor's Paste JSON would try. */
export const looksLikeJson = (text: string): boolean => /^\s*[[{]/.test(text);

/** JSON with every object's keys sorted, so two values read the same however their keys were ordered (parsing a room puts its keys in schema order). */
const canonicalJson = (value: unknown): string =>
  JSON.stringify(value, (_key, nestedValue: unknown) =>
    nestedValue && typeof nestedValue === 'object' && !Array.isArray(nestedValue)
      ? Object.fromEntries(Object.entries(nestedValue).sort(([first], [second]) => first.localeCompare(second)))
      : nestedValue);

/** Whether two rooms save as the same JSON: what a paste compares, so pasting a room's own JSON back changes nothing. */
const sameRoomJson = (first: FloorPlanRoom, second: FloorPlanRoom): boolean =>
  canonicalJson(getOptimizedFloorPlan(first)) === canonicalJson(getOptimizedFloorPlan(second));

/** The room's JSON, as Copy JSON copies it and Download JSON saves it. */
export const roomJson = (room: FloorPlanRoom): string => JSON.stringify(getOptimizedFloorPlan(room), null, 2);

/**
 * The room that clipboard text holds, at `room`'s whole-property placement, to
 * replace `room` with, or why the paste changes nothing: the JSON isn't a
 * valid room, or it is `room` already.
 */
export function pasteRoomJson(room: FloorPlanRoom, clipboard: string): JsonPasteOutcome & { shape?: FloorPlanRoom } {
  if (!looksLikeJsonObject(clipboard)) return { ok: false, message: "The clipboard doesn't hold a JSON object. Nothing was changed." };
  const result = parseFloorPlan(clipboard);
  if (!result.ok || !result.shape) return { ok: false, message: `The clipboard's JSON isn't a valid room, so nothing was changed.\n${result.message}` };
  const shape = withPlacementOf(result.shape, room);
  if (sameRoomJson(shape, room)) return { ok: true, message: "The clipboard's room already matches this one, so nothing was changed." };
  return { ok: true, shape, message: result.message };
}

/** What the host says about a room in the copied JSON, after its id and ahead of its floor plan. */
export type DescribeAssemblyRoom = (room: AssemblyRoom) => Record<string, unknown>;

const describeByName: DescribeAssemblyRoom = ({ name }) => ({ name });

/** Every room as JSON, as the floor plan view's Copy JSON copies it and Download JSON saves it. */
export const assemblyRoomsJson = (rooms: readonly AssemblyRoom[], describeRoom: DescribeAssemblyRoom = describeByName): string =>
  JSON.stringify(rooms.map(room => ({ id: room.id, ...describeRoom(room), floorPlan: getOptimizedFloorPlan(room.room) })), null, 2);
